/**
 * AIScoutingSummaryCardView - Displays AI-generated scouting summary
 *
 * Renders the AI scouting summary card with title, timestamp, content,
 * and action button matching PlayerSummaryCard styling. SE-5570: the language
 * selector renders through the shared `LanguageGenerateDropdown` molecule (on
 * the SelectDropdown / FixedPositionDropdownShell) instead of the retired
 * gluestack Menu atom.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { Copy, Sparkles } from "lucide-react-native";
import { memo } from "react";
import { Pressable } from "@/components/atoms/Pressable";

import FullScreenOverlay from "@/components/organisms/shared/FullScreenOverlay";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import type { LanguageSelectorSaveDefault } from "@/components/molecules/LanguageSelector";
import LanguageGenerateDropdown from "@/features/player-detail/components/molecules/LanguageGenerateDropdown";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

import {
  getContentArea,
  COPY_ICON_CLASS,
  getContentTextClasses,
  getIconClasses,
  getTimestampText,
  getTitleTextClasses,
  SUBTITLE_TEXT_CLASS,
} from "@/features/player-detail/utils/summaryCardStyles";

/**
 * Props for AIScoutingSummaryCardView component
 */
interface AIScoutingSummaryCardViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Truncated summary content to display */
  readonly summaryContent: string;
  /** Full summary content for modal */
  readonly fullSummaryContent: string;
  /** Formatted last generated timestamp */
  readonly lastGenerated: string | null;
  /** Whether summary is currently being generated */
  readonly isGenerating: boolean;
  /** Whether there was an error */
  readonly hasError: boolean;
  /** Error message to display */
  readonly errorMessage: string | null;
  /** Whether to show "Read more" link */
  readonly showReadMore: boolean;
  /** Whether full summary modal is open */
  readonly isModalOpen: boolean;
  /** Display label for selected language */
  readonly selectedLanguageLabel: string;
  /** List of supported languages */
  readonly supportedLanguages: { value: string; label: string }[];
  /** Currently selected language code */
  /**
   * The locale the dropdown marks, or `undefined` while nothing has resolved
   * (SE-6511). Not the generation locale — see `selectedLanguageLabel`, which
   * always names the language that will actually be produced.
   */
  readonly selectedLanguage?: string;
  /** The save-as-default row, or undefined when nothing is overridden. */
  readonly saveAsDefault?: LanguageSelectorSaveDefault;
  /** Callback when "Read more" is pressed */
  readonly onReadMore: () => void;
  /** Callback when full summary modal is closed */
  readonly onModalClose: () => void;
  /** Generates the summary in the chosen language */
  readonly onSelectLanguage: (language: string) => void;
  /** Whether the generate trigger is disabled (generating or no data) */
  readonly isButtonDisabled: boolean;
  /** Callback when copy icon is pressed */
  readonly onCopySummary: () => void;
  /** Whether the generate button is disabled due to no scouting intelligence */
  readonly isGenerateDisabled?: boolean;
  /** Message to display when generate is disabled */
  readonly disabledMessage?: string;
}

/**
 * Get container background classes for scouting summary card
 * @param isDark - Dark theme flag
 * @returns Tailwind class string for container styling
 */
const getContainerClasses = (): string =>
  "w-full rounded-lg border border-outline-card bg-surface-card p-4";

/**
 * AIScoutingSummaryCardView renders the AI scouting summary card
 * @param props - View props containing summary data and callbacks
 * @param props.isDark - Whether dark mode is active
 * @param props.summaryContent - Truncated summary content
 * @param props.fullSummaryContent - Full summary for modal
 * @param props.lastGenerated - Formatted timestamp
 * @param props.isGenerating - Whether generating
 * @param props.hasError - Whether error occurred
 * @param props.errorMessage - Error message
 * @param props.showReadMore - Whether to show read more
 * @param props.isModalOpen - Whether modal is open
 * @param props.supportedLanguages - Available languages
 * @param props.selectedLanguage - The locale the dropdown marks, or undefined
 * @param props.saveAsDefault - The save-as-default row, or undefined
 * @param props.selectedLanguageLabel - Display label for the selected language
 * @param props.onReadMore - Read more callback
 * @param props.onModalClose - Modal close callback
 * @param props.onSelectLanguage - Generates the summary in the chosen language
 * @param props.isButtonDisabled - Whether the generate trigger is disabled
 * @param props.onCopySummary - Copy summary callback
 * @param props.isGenerateDisabled - Whether generate is disabled due to no data
 * @param props.disabledMessage - Message shown when no scouting intelligence exists
 * @returns Box containing the AI summary card layout
 */
const AIScoutingSummaryCardView = ({
  isDark,
  summaryContent,
  fullSummaryContent,
  lastGenerated,
  isGenerating,
  hasError,
  errorMessage,
  showReadMore,
  isModalOpen,
  supportedLanguages,
  selectedLanguage,
  saveAsDefault,
  selectedLanguageLabel,
  onReadMore,
  onModalClose,
  onSelectLanguage,
  isButtonDisabled,
  onCopySummary,
  isGenerateDisabled = false,
  disabledMessage,
}: AIScoutingSummaryCardViewProps) => (
  <>
    <Box className={getContainerClasses()}>
      <Stack className="gap-3">
        {/* Header row with title and Read more */}
        <Stack direction="horizontal" className="items-center justify-between">
          <Stack
            direction="horizontal"
            {...{ "data-testid": TEST_ID_PLAYER_DETAIL.AI_SUMMARY_HEADING }}
            className="items-center gap-2"
          >
            <Icon as={Sparkles} size="sm" className={getIconClasses(isDark)} />
            <Text variant="title" className={getTitleTextClasses(isDark)}>
              AI Scouting Summary
            </Text>
          </Stack>
          {showReadMore && !isGenerating ? (
            <Pressable onPress={onReadMore}>
              <Text
                variant="body-strong"
                className="text-needs-semantic-primary-500"
              >
                Read more
              </Text>
            </Pressable>
          ) : null}
        </Stack>

        {/* Timestamp with copy icon — hidden in empty/disabled state to avoid duplicate "no summary" messages */}
        {!isGenerateDisabled && (isGenerating || lastGenerated) ? (
          <Stack direction="horizontal" className="items-center gap-2">
            <Text variant="unstyled" className={SUBTITLE_TEXT_CLASS}>
              {getTimestampText(isGenerating, lastGenerated)}
            </Text>
            {summaryContent && !isGenerating ? (
              <Pressable onPress={onCopySummary}>
                <Icon as={Copy} size="xs" className={COPY_ICON_CLASS} />
              </Pressable>
            ) : null}
          </Stack>
        ) : null}

        {/* Disabled message when no scouting intelligence */}
        {isGenerateDisabled && disabledMessage ? (
          <Text variant="unstyled" className={SUBTITLE_TEXT_CLASS}>
            {disabledMessage}
          </Text>
        ) : (
          getContentArea(
            isDark,
            isGenerating,
            hasError,
            errorMessage,
            summaryContent
          )
        )}

        {/* Generate button with language dropdown */}
        <Stack direction="horizontal" className="items-center pt-2">
          <LanguageGenerateDropdown
            isDark={isDark}
            supportedLanguages={supportedLanguages}
            selectedLanguage={selectedLanguage}
            selectedLanguageLabel={selectedLanguageLabel}
            saveAsDefault={saveAsDefault}
            disabled={isButtonDisabled}
            onSelectLanguage={onSelectLanguage}
            triggerTestID={TEST_ID_PLAYER_DETAIL.AI_SUMMARY_GENERATE_TRIGGER}
          />
        </Stack>
      </Stack>
    </Box>

    {/* Full Summary Modal — full-screen on mobile, centered modal on desktop (SE-3802) */}
    <FullScreenOverlay
      isOpen={isModalOpen}
      onClose={onModalClose}
      title="AI Scouting Summary"
      desktopSize="md"
    >
      <Text variant="unstyled" className={getContentTextClasses(isDark)}>
        {fullSummaryContent}
      </Text>
    </FullScreenOverlay>
  </>
);

AIScoutingSummaryCardView.displayName = "AIScoutingSummaryCardView";

export default memo(AIScoutingSummaryCardView);
